import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { FaCalendarDay } from "react-icons/fa";
import { HiChevronLeft, HiChevronRight, HiX } from "react-icons/hi";

interface DateTimePickerProps {
  minDate: Date;
  defaultValue: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  minDate,
  defaultValue,
  onChange,
  placeholder = "SELECIONE UMA DATA",
}) => {
  const [date, setDate] = useState<Date | null>(defaultValue);
  const [min, setMin] = useState<Date | null>(minDate);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date | null>(
    defaultValue ? new Date(defaultValue.setMinutes(0)) : null,
  ); // temp state for editing
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMin(minDate ?? new Date());
  }, [minDate]);

  useEffect(() => {
    setDate(defaultValue);
    setTempDate(defaultValue);
  }, [defaultValue]);

  const formatDate = (date: Date | null) => {
    if (!date) return <p className="text-xs">{placeholder}</p>;
    return (
      <>
        <p className="text-xs">
          {moment(date).format("D [de] MMMM, ddd")}{" "}
          <strong> {moment(date).format("HH:mm")}</strong>
        </p>
      </>
    );
  };

  // Detecta mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // Fecha o calendário ao clicar fora
  React.useEffect(() => {
    if (!calendarOpen) return;
    function handle(e: MouseEvent) {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        const calendar = document.getElementById("mui-calendar-popper");
        if (calendar && calendar.contains(e.target as Node)) {
          return;
        }
        setCalendarOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [calendarOpen]);

  // Ao abrir o calendário, inicializa tempDate com o valor atual
  useEffect(() => {
    if (calendarOpen) {
      setTempDate(date ?? new Date());
    }
  }, [calendarOpen, date]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="relative inline-block">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => {
            if (date) {
              setDate(null);
              onChange(null);
            } else {
              setCalendarOpen((v) => !v);
            }
          }}
          className="group relative flex w-full flex-row items-center rounded-full border border-gray-300 bg-gray-300 py-0.5 pl-1 pr-0 text-black"
        >
          <div
            className={
              date === null
                ? "group-hover:text-green-500"
                : "hover:text-red-500"
            }
          >
            {formatDate(date)}
          </div>
          <div
            className={`relative ml-1 flex h-7 w-7 items-center justify-center rounded-full border border-white bg-white transition ${
              date === null
                ? "group-hover:scale-110 group-hover:border-green-500 group-hover:text-green-500"
                : "hover:scale-110 hover:border-red-500 hover:text-red-500"
            }`}
          >
            {date ? <HiX /> : <FaCalendarDay />}
          </div>
        </button>
        {calendarOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
              onClick={() => setCalendarOpen(false)}
            />
            {/* Calendário flutuante */}
            <div
              id="mui-calendar-popper"
              className={`fixed z-50 ${
                isMobile
                  ? "left-1/2 top-1/2 w-[95vw] max-w-xs -translate-x-1/2 -translate-y-1/2"
                  : `absolute mt-2 ${
                      buttonRef.current &&
                      buttonRef.current.getBoundingClientRect().right >
                        window.innerWidth - 320
                        ? "right-0"
                        : "left-0"
                    }`
              } rounded-xl bg-white/80 p-2 shadow-2xl backdrop-blur-lg`}
              style={{ minWidth: 280 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-start">
                <DemoItem>
                  <DateCalendar
                    views={["day"]}
                    value={tempDate ?? new Date()}
                    minDate={min ?? undefined}
                    onChange={(newDate) => {
                      if (newDate) {
                        // Mantém hora/minuto se já existia
                        const updated = new Date(newDate as Date);
                        if (tempDate) {
                          updated.setHours(
                            tempDate.getHours(),
                            tempDate.getMinutes(),
                            0,
                            0,
                          );
                        }
                        setTempDate(updated);
                      }
                    }}
                    sx={{
                      "& .Mui-selected, & .MuiPickersDay-root.Mui-selected": {
                        backgroundColor: "#dd1a30",
                        color: "#fff",
                        "&:hover, &:focus": {
                          backgroundColor: "#b31323",
                        },
                      },
                      "& .MuiPickersDay-root:not(.Mui-selected):hover": {
                        backgroundColor: "#ffe5e8",
                      },
                      "& .MuiPickersDay-today": {
                        borderColor: "#dd1a30",
                      },
                    }}
                  />
                </DemoItem>
                <div className="mx-auto flex flex-col items-center justify-center">
                  <label className="mb-1 text-xs font-medium text-gray-700">
                    Hora
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      className="rounded-full p-2 text-lg font-bold hover:bg-gray-200"
                      type="button"
                      onClick={() => {
                        if (!tempDate) return;
                        const updated = new Date(tempDate);
                        let newHour = updated.getHours() - 1;
                        if (newHour < 0) newHour = 23;
                        updated.setHours(newHour, 0, 0, 0);
                        setTempDate(updated);
                      }}
                    >
                      <HiChevronLeft color="#666" size={20} />
                    </button>
                    <span className="px-2 font-mono text-lg text-zinc-700">
                      {tempDate
                        ? tempDate.getHours().toString().padStart(2, "0")
                        : "12"}
                      h
                    </span>
                    <button
                      className="rounded-full p-2 text-lg font-bold hover:bg-gray-200"
                      type="button"
                      onClick={() => {
                        if (!tempDate) return;
                        const updated = new Date(tempDate);
                        let newHour = updated.getHours() + 1;
                        if (newHour > 23) newHour = 0;
                        updated.setHours(newHour, 0, 0, 0);
                        setTempDate(updated);
                      }}
                    >
                      <HiChevronRight color="#666" size={20} />
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="rounded-full px-3 py-1 text-sm hover:bg-gray-200"
                  onClick={() => setCalendarOpen(false)}
                  type="button"
                >
                  Cancelar
                </button>
                <button
                  className="rounded-full bg-primary-500 px-3 py-1 text-sm text-white hover:bg-zinc-600"
                  onClick={() => {
                    setDate(tempDate);
                    onChange(tempDate);
                    setCalendarOpen(false);
                  }}
                  type="button"
                  disabled={!tempDate}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default DateTimePicker;
